import React from 'react';
import { FileText, Clock, BarChart2, AlertTriangle } from 'lucide-react';

interface Props {
  onStartExam: () => void;
  onViewReport: () => void;
}

export default function MockExamIntro({ onStartExam, onViewReport }: Props) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-xl w-full space-y-5 animate-scale-in">
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <FileText size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Stage 4 — Mock Exam</h1>
              <p className="text-sm text-muted-foreground">Full exam simulation · 3 passages · 40 questions · 60 minutes</p>
            </div>
          </div>

          {/* Conditions */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { id: 'mc-time', icon: <Clock size={16} className="text-primary" />, label: '60 minutes', sub: 'Strict — no extensions' },
              { id: 'mc-q', icon: <FileText size={16} className="text-accent" />, label: '40 questions', sub: '3 passages' },
              { id: 'mc-band', icon: <BarChart2 size={16} className="text-warning" />, label: 'Band report', sub: 'Instant after submit' },
            ].map((c) => (
              <div key={c.id} className="p-3 rounded-xl bg-muted border border-border text-center">
                <div className="flex justify-center mb-1.5">{c.icon}</div>
                <p className="text-sm font-semibold text-foreground">{c.label}</p>
                <p className="text-xs text-muted-foreground">{c.sub}</p>
              </div>
            ))}
          </div>

          <div className="p-3.5 rounded-xl bg-warning-bg border border-warning/30 mb-5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-warning mb-1">Exam-day conditions apply</p>
                <p className="text-xs text-warning/80 leading-relaxed">
                  No pause, no hints, no walkthrough access. The timer starts immediately. Close all other tabs. This mock replicates the computer-delivered IELTS interface exactly.
                </p>
              </div>
            </div>
          </div>

          {/* Band conversion table */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2.5">Official Raw → Band Conversion</p>
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { id: 'bc-90', raw: '38–40', band: '8.5–9.0', highlight: false },
                { id: 'bc-85', raw: '35–37', band: '8.0', highlight: true },
                { id: 'bc-75', raw: '32–34', band: '7.5', highlight: false },
                { id: 'bc-70', raw: '28–31', band: '7.0', highlight: false },
                { id: 'bc-65', raw: '23–27', band: '6.0–6.5', highlight: false },
              ].map((row) => (
                <div
                  key={row.id}
                  className={`p-2.5 rounded-lg text-center border ${row.highlight ? 'border-accent bg-success-bg' : 'border-border bg-muted'}`}
                >
                  <p className={`text-sm font-bold tabular-nums ${row.highlight ? 'text-accent' : 'text-foreground'}`}>{row.band}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{row.raw} correct</p>
                  {row.highlight && <p className="text-xs text-accent font-semibold mt-0.5">← Target</p>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Your current estimate: Band 7.5 (32/40) · Need 3 more for Band 8.0</p>
          </div>

          <div className="flex gap-3">
            <button onClick={onViewReport} className="btn-outline flex-1 justify-center">
              View Last Report
            </button>
            <button onClick={onStartExam} className="btn-primary flex-1 justify-center py-3">
              Enter Mock Exam →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}